import React, { useState, useEffect, useCallback, memo } from 'react'
import {
  BookOpen, Search, Clock, Eye, Heart, ArrowLeft,
  FileText, Loader2, Sparkles, TrendingUp, Plus, Pencil, Trash2, Send, EyeOff
} from 'lucide-react'
import api from '../../services/api'
import { useSearchParams } from 'react-router-dom'
import GovLayout, { GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { useAuthStore } from '@/stores/authStore'
import ArticleFormModal from '@/components/knowledge/ArticleFormModal'

interface Category {
  id: number
  name: string
  slug: string
  description?: string
  icon?: string
  imageUrl?: string
  articleCount?: number
  children?: Category[]
}

interface Article {
  id: number
  title: string
  slug: string
  content: string
  excerpt?: string
  featuredImage?: string
  category?: Category
  author?: { id: number; firstName: string; lastName: string }
  tags?: string[]
  viewCount: number
  likeCount: number
  isFeatured: boolean
  isPublished: boolean
  publishedAt?: string
  createdAt: string
}

// Format date helper
const formatDate = (dateStr: string) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Format number with K/M
const formatNumber = (num: number) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toString()
}

// Category icons based on slug
const getCategoryIcon = (slug: string) => {
  const icons: Record<string, string> = {
    'tb-awareness': '📚',
    'prevention': '🛡️',
    'treatment': '💊',
    'nutrition': '🥗',
    'public-health': '🏥',
    'symptoms': '🤒',
    'diagnosis': '🔬',
    'medicines': '💉',
    'lifestyle': '🏃',
    'support': '🤝'
  }
  return icons[slug] || '📖'
}

// ================================================================================
// ARTICLE CARD COMPONENT - Single vertical layout matching Feed design
// ================================================================================
const ArticleCard = memo(({ article, featured = false, onClick }: { article: Article; featured?: boolean; onClick: () => void }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
  >
    {/* Image - Full width on top */}
    {article.featuredImage ? (
      <div className="h-48">
        <img
          src={article.featuredImage.startsWith('http') ? article.featuredImage : article.featuredImage.startsWith('/uploads/') ? article.featuredImage : `/uploads/${article.featuredImage}`}
          alt={article.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    ) : (
      <div className="h-48 bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
        <BookOpen className="w-12 h-12 text-white/70" />
      </div>
    )}

    {/* Content - Below image */}
    <div className="p-4">
      {/* Tags */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {article.isFeatured && (
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">Featured</span>
        )}
        {article.category && (
          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
            {article.category.name}
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="font-semibold text-base mb-2 text-gray-900 line-clamp-2">{article.title}</h3>

      {/* Description */}
      {article.excerpt && (
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{article.excerpt}</p>
      )}

      {/* Metadata - Views, Likes, Date */}
      <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Eye className="w-3.5 h-3.5" />
            {formatNumber(article.viewCount || 0)}
          </span>
          <span className="flex items-center gap-1">
            <Heart className="w-3.5 h-3.5" />
            {formatNumber(article.likeCount || 0)}
          </span>
        </div>
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {formatDate(article.publishedAt || article.createdAt)}
        </span>
      </div>
    </div>
  </div>
))

// ================================================================================
// CATEGORY CARD COMPONENT
// ================================================================================
const CategoryCard = memo(({ category, onClick }: { category: Category; onClick: () => void }) => (
  <button
    onClick={onClick}
    className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center cursor-pointer hover:shadow-md hover:border-blue-200 transition-all w-full"
  >
    <div className="text-4xl mb-2">{getCategoryIcon(category.slug)}</div>
    <p className="font-medium text-gray-900 text-sm">{category.name}</p>
    <p className="text-xs text-gray-500 mt-1">{category.articleCount || 0} articles</p>
  </button>
))

// ================================================================================
// ARTICLE DETAIL VIEW
// ================================================================================
const ArticleDetailView = memo(({ article, onBack, onLike }: { article: Article; onBack: () => void; onLike: () => void }) => (
  <div className="max-w-4xl mx-auto">
    <button
      onClick={onBack}
      className="flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-4 transition-colors"
    >
      <ArrowLeft className="w-5 h-5" />
      Back to Articles
    </button>

    <GovCard>
      {article.featuredImage && (
        <img
          src={article.featuredImage.startsWith('http') ? article.featuredImage : article.featuredImage.startsWith('/uploads/') ? article.featuredImage : `/uploads/${article.featuredImage}`}
          alt={article.title}
          className="w-full h-64 object-cover rounded-t-xl -mt-4 -mx-4 mb-4"
        />
      )}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {article.isFeatured && (
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-sm rounded-full font-medium">Featured</span>
        )}
        {article.category && (
          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-sm rounded-full">
            {article.category.name}
          </span>
        )}
      </div>

      <h1 className="text-xl font-bold mb-3 text-gray-900">{article.title}</h1>

      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-4">
        {article.author && (
          <span>By {article.author.firstName} {article.author.lastName}</span>
        )}
        <span className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          {formatDate(article.publishedAt || article.createdAt)}
        </span>
        <span className="flex items-center gap-1">
          <Eye className="w-4 h-4" />
          {formatNumber(article.viewCount || 0)} views
        </span>
      </div>

      {article.tags && article.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {article.tags.map((tag, index) => (
            <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
              #{tag}
            </span>
          ))}
        </div>
      )}

      <div className="prose prose-sm max-w-none text-gray-700">
        <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: article.content.replace(/\n/g, '<br/>') }} />
      </div>

      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
        <GovButton onClick={onLike} variant="secondary" className="flex items-center gap-2">
          <Heart className="w-4 h-4" />
          <span>{formatNumber(article.likeCount || 0)} likes</span>
        </GovButton>
        <GovButton onClick={onBack} variant="ghost">
          Close
        </GovButton>
      </div>
    </GovCard>
  </div>
))

// ================================================================================
// SEARCH RESULTS VIEW
// ================================================================================
const SearchResultsView = memo(({
  query,
  results,
  isSearching,
  onBack,
  onArticleClick
}: {
  query: string
  results: Article[]
  isSearching: boolean
  onBack: () => void
  onArticleClick: (article: Article) => void
}) => (
  <div className="max-w-4xl mx-auto">
    <div className="flex items-center gap-3 mb-4">
      <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
        <ArrowLeft className="w-5 h-5 text-gray-600" />
      </button>
      <GovPageTitle title={`Search: "${query}"`} icon={Search} />
    </div>

    {isSearching ? (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
        <p className="text-gray-500 mt-2">Searching...</p>
      </div>
    ) : results.length === 0 ? (
      <GovEmptyState
        icon="🔍"
        title="No Results Found"
        description={`No articles match "${query}"`}
      />
    ) : (
      <div className="space-y-3">
        {results.map(article => (
          <ArticleCard
            key={article.id}
            article={article}
            onClick={() => onArticleClick(article)}
          />
        ))}
      </div>
    )}
  </div>
))

// ================================================================================
// CATEGORY VIEW
// ================================================================================
const CategoryView = memo(({
  category,
  articles,
  loading,
  hasMore,
  onLoadMore,
  onBack,
  onArticleClick
}: {
  category: Category
  articles: Article[]
  loading: boolean
  hasMore: boolean
  onLoadMore: () => void
  onBack: () => void
  onArticleClick: (article: Article) => void
}) => (
  <div className="max-w-4xl mx-auto">
    <div className="flex items-center gap-3 mb-4">
      <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
        <ArrowLeft className="w-5 h-5 text-gray-600" />
      </button>
      <div className="flex items-center gap-2">
        <span className="text-3xl">{getCategoryIcon(category.slug)}</span>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{category.name}</h1>
          {category.description && (
            <p className="text-sm text-gray-500">{category.description}</p>
          )}
        </div>
      </div>
    </div>

    {loading ? (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
      </div>
    ) : articles.length === 0 ? (
      <GovEmptyState
        icon="📄"
        title="No Articles Yet"
        description="Check back soon for new content"
      />
    ) : (
      <div className="space-y-3">
        {articles.map(article => (
          <ArticleCard
            key={article.id}
            article={article}
            onClick={() => onArticleClick(article)}
          />
        ))}
        {hasMore && (
          <div className="text-center mt-4">
            <GovButton onClick={onLoadMore} variant="secondary">
              Load More Articles
            </GovButton>
          </div>
        )}
      </div>
    )}
  </div>
))

// ================================================================================
// MAIN KNOWLEDGE PAGE
// ================================================================================
const KnowledgePage = () => {
  const { isAuthenticated, user } = useAuthStore()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
  const [categories, setCategories] = useState<Category[]>([])
  const [articles, setArticles] = useState<Article[]>([])
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([])
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Article[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingArticles, setLoadingArticles] = useState(false)
  const [activeTab, setActiveTab] = useState<'all' | 'featured' | 'popular' | 'my'>('all')
  const [articlePage, setArticlePage] = useState(0)
  const [hasMoreArticles, setHasMoreArticles] = useState(true)

  // Article authoring/management state
  const [showForm, setShowForm] = useState(false)
  const [editingArticle, setEditingArticle] = useState<Article | null>(null)
  const [myArticles, setMyArticles] = useState<Article[]>([])
  const [myLoading, setMyLoading] = useState(false)
  const [actingId, setActingId] = useState<number | null>(null)

  // Deep-link from search: ?article=<slug> opens the article detail directly.
  const [searchParams] = useSearchParams()

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories()
    fetchFeaturedArticles()
  }, [])

  // Open an article linked via ?article=<slug> (e.g. from global search results).
  useEffect(() => {
    const slug = searchParams.get('article')
    if (!slug) return
    let cancelled = false
    api
      .get(`/knowledge/articles/slug/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (!cancelled && res.data.success && res.data.data) {
          setSelectedArticle(res.data.data as Article)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [searchParams])

  // Fetch articles when category or tab changes
  useEffect(() => {
    if (activeTab === 'my') {
      fetchMyArticles()
    } else if (!selectedCategory && activeTab === 'all') {
      fetchArticles(0)
    }
  }, [selectedCategory, activeTab])

  const fetchCategories = async () => {
    try {
      const response = await api.get('/knowledge/categories')
      if (response.data.success) {
        setCategories(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const fetchArticles = useCallback(async (page = 0, append = false) => {
    if (page === 0) setLoadingArticles(true)
    try {
      let url = `/knowledge/articles?page=${page}&size=10`
      if (selectedCategory) {
        url = `/knowledge/categories/${selectedCategory.id}/articles?page=${page}&size=10`
      } else if (activeTab === 'featured') {
        url = `/knowledge/articles/featured?page=${page}&size=10`
      } else if (activeTab === 'popular') {
        url = `/knowledge/articles/most-viewed?page=${page}&size=10`
      }

      const response = await api.get(url)
      if (response.data.success) {
        const newArticles = response.data.data?.content || []
        if (append) {
          setArticles(prev => [...prev, ...newArticles])
        } else {
          setArticles(newArticles)
        }
        setHasMoreArticles(newArticles.length === 10)
        setArticlePage(page)
      }
    } catch (error) {
      console.error('Error fetching articles:', error)
    } finally {
      setLoadingArticles(false)
      setLoading(false)
    }
  }, [selectedCategory, activeTab])

  const fetchFeaturedArticles = async () => {
    try {
      const response = await api.get('/knowledge/articles/featured?page=0&size=5')
      if (response.data.success) {
        setFeaturedArticles(response.data.data?.content || [])
      }
    } catch (error) {
      console.error('Error fetching featured articles:', error)
    }
  }

  const searchArticles = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }
    setIsSearching(true)
    try {
      const response = await api.get(`/knowledge/articles/search?keyword=${encodeURIComponent(query)}&page=0&size=20`)
      if (response.data.success) {
        setSearchResults(response.data.data?.content || [])
      }
    } catch (error) {
      console.error('Error searching articles:', error)
    } finally {
      setIsSearching(false)
    }
  }, [])

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        searchArticles(searchQuery)
      }
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [searchQuery, searchArticles])

  const handleCategoryClick = useCallback((category: Category) => {
    setSelectedCategory(category)
    setSelectedArticle(null)
    setSearchQuery('')
    setSearchResults([])
    setActiveTab('all')
  }, [])

  const handleArticleClick = useCallback(async (article: Article) => {
    try {
      await api.post(`/knowledge/articles/${article.id}/view`)
    } catch (error) {
      console.error('Error incrementing view count:', error)
    }
    setSelectedArticle(article)
  }, [])

  const handleLikeArticle = useCallback(async () => {
    if (!selectedArticle) return
    try {
      const response = await api.post(`/knowledge/articles/${selectedArticle.id}/like`)
      if (response.data.success) {
        setSelectedArticle({
          ...selectedArticle,
          likeCount: response.data.data.likeCount
        })
      }
    } catch (error) {
      console.error('Error liking article:', error)
    }
  }, [selectedArticle])

  const handleBackToList = useCallback(() => {
    setSelectedArticle(null)
  }, [])

  const handleBackToCategories = useCallback(() => {
    setSelectedCategory(null)
    setSelectedArticle(null)
    setSearchQuery('')
    setSearchResults([])
    fetchArticles(0)
  }, [fetchArticles])

  const loadMoreArticles = useCallback(() => {
    if (!loadingArticles && hasMoreArticles) {
      fetchArticles(articlePage + 1, true)
    }
  }, [loadingArticles, hasMoreArticles, articlePage, fetchArticles])

  const handleTabChange = useCallback((tab: 'all' | 'featured' | 'popular' | 'my') => {
    setActiveTab(tab)
    if (tab === 'my') {
      fetchMyArticles()
    } else {
      fetchArticles(0)
    }
  }, [fetchArticles])

  // --- Article authoring / management (author-gated; backend enforces ownership) ---
  const fetchMyArticles = useCallback(async () => {
    setMyLoading(true)
    try {
      const response = await api.get('/knowledge/articles/my?page=0&size=50')
      if (response.data.success) {
        setMyArticles(response.data.data?.content || [])
      }
    } catch (error) {
      console.error('Error fetching my articles:', error)
      setMyArticles([])
    } finally {
      setMyLoading(false)
    }
  }, [])

  const handleCreateArticle = useCallback(() => {
    setEditingArticle(null)
    setShowForm(true)
  }, [])

  const handleEditArticle = useCallback((article: Article) => {
    setEditingArticle(article)
    setShowForm(true)
  }, [])

  const handleFormSuccess = useCallback(() => {
    // Refresh whatever view is active
    fetchFeaturedArticles()
    if (activeTab === 'my') {
      fetchMyArticles()
    } else {
      fetchArticles(0)
    }
  }, [activeTab, fetchArticles, fetchMyArticles])

  const handleDeleteArticle = useCallback(async (article: Article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return
    setActingId(article.id)
    try {
      await api.delete(`/knowledge/articles/${article.id}`)
      setMyArticles(prev => prev.filter(a => a.id !== article.id))
      fetchFeaturedArticles()
      if (activeTab !== 'my') fetchArticles(0)
    } catch (error: any) {
      console.error('Error deleting article:', error)
      alert(error?.response?.data?.message || 'Failed to delete article')
    } finally {
      setActingId(null)
    }
  }, [activeTab, fetchArticles])

  const handlePublishArticle = useCallback(async (article: Article) => {
    setActingId(article.id)
    try {
      await api.post(`/knowledge/articles/${article.id}/publish`)
      setMyArticles(prev => prev.map(a => a.id === article.id ? { ...a, isPublished: true } : a))
      fetchFeaturedArticles()
    } catch (error: any) {
      console.error('Error publishing article:', error)
      alert(error?.response?.data?.message || 'Failed to publish article')
    } finally {
      setActingId(null)
    }
  }, [fetchFeaturedArticles])

  const handleUnpublishArticle = useCallback(async (article: Article) => {
    setActingId(article.id)
    try {
      await api.post(`/knowledge/articles/${article.id}/unpublish`)
      setMyArticles(prev => prev.map(a => a.id === article.id ? { ...a, isPublished: false } : a))
      fetchFeaturedArticles()
    } catch (error: any) {
      console.error('Error unpublishing article:', error)
      alert(error?.response?.data?.message || 'Failed to unpublish article')
    } finally {
      setActingId(null)
    }
  }, [fetchFeaturedArticles])

  const clearSearch = useCallback(() => {
    setSearchQuery('')
    setSearchResults([])
  }, [])

  // Article Detail View
  if (selectedArticle) {
    return (
      <GovLayout>
        <ArticleDetailView
          article={selectedArticle}
          onBack={handleBackToList}
          onLike={handleLikeArticle}
        />
      </GovLayout>
    )
  }

  // Search Results View
  if (searchQuery.trim() && (searchResults.length > 0 || isSearching)) {
    return (
      <GovLayout>
        <SearchResultsView
          query={searchQuery}
          results={searchResults}
          isSearching={isSearching}
          onBack={clearSearch}
          onArticleClick={handleArticleClick}
        />
      </GovLayout>
    )
  }

  // Category Articles View
  if (selectedCategory) {
    return (
      <GovLayout>
        <CategoryView
          category={selectedCategory}
          articles={articles}
          loading={loadingArticles}
          hasMore={hasMoreArticles}
          onLoadMore={loadMoreArticles}
          onBack={handleBackToCategories}
          onArticleClick={handleArticleClick}
        />
      </GovLayout>
    )
  }

  // Main View
  return (
    <GovLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <GovPageTitle
          title="TB Knowledge Center"
          subtitle="Learn about tuberculosis prevention, symptoms, and treatment"
          icon={BookOpen}
        />

        {/* Create Article — available to every logged-in user */}
        {isAuthenticated && (
          <div className="flex justify-end">
            <GovButton onClick={handleCreateArticle} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create Article
            </GovButton>
          </div>
        )}

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search articles about TB..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm"
          />
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
            <p className="text-gray-500 mt-2">Loading knowledge base...</p>
          </div>
        ) : (
          <>
            {/* Featured Articles */}
            {featuredArticles.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <h2 className="text-base font-bold text-gray-900">Featured Articles</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featuredArticles.slice(0, 3).map(article => (
                    <ArticleCard
                      key={article.id}
                      article={article}
                      featured
                      onClick={() => handleArticleClick(article)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Categories */}
            <section>
              <h2 className="text-base font-bold text-gray-900 mb-3">Categories</h2>
              {categories.length === 0 ? (
                <GovEmptyState
                  icon="📚"
                  title="No Categories"
                  description="Check back soon for TB knowledge articles"
                />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {categories.map(category => (
                    <CategoryCard
                      key={category.id}
                      category={category}
                      onClick={() => handleCategoryClick(category)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Tabs */}
            <section>
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  onClick={() => handleTabChange('all')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === 'all'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  All Articles
                </button>
                <button
                  onClick={() => handleTabChange('featured')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === 'featured'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Featured
                </button>
                <button
                  onClick={() => handleTabChange('popular')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === 'popular'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <TrendingUp className="w-4 h-4 inline mr-1" />
                  Most Viewed
                </button>
                {isAuthenticated && (
                  <button
                    onClick={() => handleTabChange('my')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'my'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <FileText className="w-4 h-4 inline mr-1" />
                    My Articles
                  </button>
                )}
              </div>

              {activeTab === 'my' ? (
                // --- My Articles: management list with author-gated actions ---
                myLoading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
                  </div>
                ) : myArticles.length === 0 ? (
                  <GovEmptyState
                    icon="📝"
                    title="No Articles Yet"
                    description="Create your first article using the button above."
                  />
                ) : (
                  <div className="space-y-3">
                    {myArticles.map(article => {
                      const busy = actingId === article.id
                      return (
                        <div key={article.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="font-semibold text-gray-900 truncate">{article.title}</h3>
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  article.isPublished ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {article.isPublished ? 'Published' : 'Draft'}
                                </span>
                                {article.isFeatured && (
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">Featured</span>
                                )}
                              </div>
                              {article.excerpt && <p className="text-sm text-gray-600 line-clamp-2 mb-1">{article.excerpt}</p>}
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                {article.category && <span>{article.category.name}</span>}
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3.5 h-3.5" />
                                  {formatNumber(article.viewCount || 0)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5" />
                                  {formatDate(article.publishedAt || article.createdAt)}
                                </span>
                              </div>
                            </div>
                          </div>
                          {/* Author actions (backend also enforces ownership) */}
                          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                            <GovButton size="sm" variant="secondary" onClick={() => handleEditArticle(article)} disabled={busy}>
                              <Pencil className="w-3.5 h-3.5 mr-1 inline" /> Edit
                            </GovButton>
                            {article.isPublished ? (
                              <GovButton size="sm" variant="ghost" onClick={() => handleUnpublishArticle(article)} disabled={busy}>
                                <EyeOff className="w-3.5 h-3.5 mr-1 inline" /> Unpublish
                              </GovButton>
                            ) : (
                              <GovButton size="sm" variant="secondary" onClick={() => handlePublishArticle(article)} disabled={busy}>
                                <Send className="w-3.5 h-3.5 mr-1 inline" /> Publish
                              </GovButton>
                            )}
                            <GovButton size="sm" variant="ghost" onClick={() => handleDeleteArticle(article)} disabled={busy}>
                              <Trash2 className="w-3.5 h-3.5 mr-1 inline" /> Delete
                            </GovButton>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )
              ) : loadingArticles ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
                </div>
              ) : articles.length === 0 ? (
                <GovEmptyState
                  icon="📄"
                  title="No Articles Available"
                  description="Check back soon for new TB knowledge content"
                />
              ) : (
                <div className="space-y-3">
                  {articles.map(article => (
                    <ArticleCard
                      key={article.id}
                      article={article}
                      onClick={() => handleArticleClick(article)}
                    />
                  ))}
                  {hasMoreArticles && (
                    <div className="text-center mt-4">
                      <GovButton onClick={loadMoreArticles} variant="secondary">
                        {loadingArticles ? 'Loading...' : 'Load More Articles'}
                      </GovButton>
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* Create/Edit article modal (text-only) */}
      <ArticleFormModal
        open={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={handleFormSuccess}
        categories={categories}
        editingArticle={editingArticle}
        isAdmin={isAdmin}
      />
    </GovLayout>
  )
}

export default KnowledgePage